import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload, Loader2, Search } from "lucide-react";
import { OverlayPage } from "@/components/layout/OverlayPage";
import { OverlayContent } from "@/components/layout/OverlayContent";
import { useLanguage } from "@/contexts/LanguageContext";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { ExcelUploadTab, ExcelUploadTabRef } from "@/components/bulk/ExcelUploadTab";
import { SmartPasteTab, SmartPasteTabRef } from "@/components/bulk/SmartPasteTab";
import { OrderReviewTableEnhanced } from "@/components/bulk/OrderReviewTableEnhanced";
import { BulkImportSummary } from "@/components/bulk/BulkImportSummary";
import { BulkParsingOverlay } from "@/components/bulk/BulkParsingOverlay";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { bulkInsertOrders, ParsedOrder } from "@/lib/bulkOrderUtils";
import { detectDuplicates, markDuplicates } from "@/lib/duplicateDetection";
import { useQueryClient } from "@tanstack/react-query";
import { useOverlayState } from "@/hooks/useOverlayState";

type Step = "upload" | "parsing" | "review" | "complete";

export default function BulkUpload() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Derive step from URL or default to "upload"
  const urlStep = searchParams.get("step") as Step | null;
  const step: Step = urlStep === "review" || urlStep === "complete" || urlStep === "parsing" ? urlStep : "upload";

  const [parsedOrders, setParsedOrders] = useState<ParsedOrder[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [aiProcessingTimeMs, setAiProcessingTimeMs] = useState<number>(0);
  const [uploadMode, setUploadMode] = useState<string>("excel");
  const [isParsing, setIsParsing] = useState(false);
  const [parseProgress, setParseProgress] = useState<{ completed: number; total: number }>({ completed: 0, total: 0 });
  const [estimatedCount, setEstimatedCount] = useState<number | undefined>();
  const [hasUploadContent, setHasUploadContent] = useState(false);
  
  // Refs for child tab components (imperative handle)
  const excelTabRef = useRef<ExcelUploadTabRef>(null);
  const smartPasteTabRef = useRef<SmartPasteTabRef>(null);
  
  // Selection state lifted from OrderReviewTableEnhanced for footer action authority
  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedOrders, setSelectedOrders] = useState<ParsedOrder[]>([]);
  
  const handleSelectionChange = useCallback((count: number, orders: ParsedOrder[]) => {
    setSelectedCount(count);
    setSelectedOrders(orders);
  }, []);

  // Use overlay state machine for proper dirty/processing/success handling
  const overlay = useOverlayState();

  // Track dirty state based on step and data
  // Dirty = has parsed orders AND not on complete step
  useEffect(() => {
    const hasData = parsedOrders.length > 0 && step !== "complete";
    overlay.setDirty(hasData);
  }, [parsedOrders.length, step, overlay]);

  const handleParsed = useCallback(
    async (orders: ParsedOrder[], orderSummary: any) => {
      if (!user) return;

      // Store AI processing time from the summary
      if (orderSummary?.processingTimeMs) {
        setAiProcessingTimeMs(orderSummary.processingTimeMs);
      }

      // Initialize enhanced fields for all orders
      const enhancedOrders: ParsedOrder[] = orders.map((order) => ({
        ...order,
        errors: [],
        isDuplicate: false,
        isEdited: false,
        rowState: order.valid ? ("valid" as const) : order.warnings.length > 0 ? ("warning" as const) : ("error" as const),
      }));

      // Detect duplicates
      toast({
        title: t("bulk.checkingDuplicates") || "Checking for duplicates...",
        description: t("bulk.validatingOrders") || "Validating orders against existing data",
      });

      const duplicateResult = await detectDuplicates(enhancedOrders, user.id);
      const finalOrders = markDuplicates(enhancedOrders, duplicateResult);

      // Update summary with duplicate info
      const updatedSummary = {
        ...orderSummary,
        duplicates: duplicateResult.internalDuplicates.length + duplicateResult.databaseDuplicates.length,
      };

      setParsedOrders(finalOrders);
      setSummary(updatedSummary);
      setSearchParams({ step: "review" });

      // Show duplicate warning if any found
      if (updatedSummary.duplicates > 0) {
        toast({
          title: t("bulk.duplicatesDetected") || "Duplicates detected",
          description: `${t("bulk.foundDuplicates") || "Found"} ${updatedSummary.duplicates} ${t("bulk.duplicateOrders") || "duplicate orders"}`,
          variant: "destructive",
        });
      }
    },
    [user, toast, t, setSearchParams]
  );

  const handleImport = useCallback(
    async (selectedOrders: ParsedOrder[]) => {
      if (!user) {
        toast({
          title: t("common.error"),
          description: t("auth.mustBeLoggedIn") || "You must be logged in to import orders",
          variant: "destructive",
        });
        return;
      }

      // Start processing state - blocks close during import
      overlay.startProcessing();
      setIsImporting(true);
      const startTime = Date.now();

      toast({
        title: t("bulk.importing"),
        description: `${t("orders.processing")} ${selectedOrders.length} ${t("orders.ordersLabel").toLowerCase()}...`,
      });

      try {
        const result = await bulkInsertOrders(selectedOrders, user.id);
        const endTime = Date.now();
        const dbInsertTimeMs = endTime - startTime;

        // Total time = AI processing time + database insert time
        const totalProcessingTimeMs = aiProcessingTimeMs + dbInsertTimeMs;

        setImportResult({
          ...result,
          processingTimeMs: totalProcessingTimeMs,
        });
        
        // SUCCESS CLOSURE LAW: Mark as complete before changing step
        // This ensures the overlay can close immediately without confirmation
        overlay.complete();
        setSearchParams({ step: "complete" });

        if (result.success > 0) {
          toast({
            title: t("common.success"),
            description: `${t("bulk.successfullyImported") || "Successfully imported"} ${result.success} ${t("orders.ordersLabel").toLowerCase()}`,
          });
          queryClient.invalidateQueries({ queryKey: ["orders"] });
        } else {
          toast({
            title: t("bulk.importFailed") || "Import failed",
            description: t("bulk.noOrdersImported") || "No orders were imported",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Import error:", error);
        overlay.fail(error instanceof Error ? error.message : "Unknown error");
        toast({
          title: t("common.error"),
          description: t("bulk.failedToImport") || "Failed to import orders",
          variant: "destructive",
        });
      } finally {
        setIsImporting(false);
      }
    },
    [user, aiProcessingTimeMs, toast, t, setSearchParams, queryClient, overlay]
  );

  const handleClose = () => {
    navigate("/orders");
  };

  const handleBackToUpload = () => {
    setIsParsing(false);
    setParseProgress({ completed: 0, total: 0 });
    setSearchParams({});
  };

  const handleCancelParsing = () => {
    // Cancel parsing and return to upload
    excelTabRef.current?.cancel?.();
    smartPasteTabRef.current?.cancel?.();
    setIsParsing(false);
    setParseProgress({ completed: 0, total: 0 });
    setSearchParams({});
  };

  // Trigger parsing from footer CTA
  const handleReviewOrders = async () => {
    setIsParsing(true);
    setSearchParams({ step: "parsing" });
    
    // Get estimated count from active tab
    if (uploadMode === "excel") {
      setEstimatedCount(excelTabRef.current?.getEstimatedCount?.());
      await excelTabRef.current?.parse?.();
    } else {
      setEstimatedCount(smartPasteTabRef.current?.getEstimatedCount?.());
      await smartPasteTabRef.current?.parse?.();
    }
  };

  // Reset content state when switching tabs
  const handleUploadModeChange = (mode: string) => {
    setUploadMode(mode);
    setHasUploadContent(false);
  };

  const breadcrumbs = [{ label: t("nav.orders"), href: "/orders" }, { label: t("orders.bulkUpload") }];

  // Dynamic primary action based on step - footer is the single authority
  const getPrimaryAction = () => {
    if (step === "complete") {
      return {
        label: t("common.close") || "Close",
        onClick: handleClose,
      };
    }
    if (step === "parsing") {
      // No primary action during parsing - overlay handles everything
      return {
        label: t("bulk.processing") || "Processing...",
        onClick: () => {},
        disabled: true,
        loading: true,
        icon: <Loader2 className="h-4 w-4 animate-spin" />,
      };
    }
    if (step === "review") {
      return {
        label: isImporting 
          ? t("bulk.importing") || "Importing..."
          : (t("bulk.importSelected") || "Import {{count}} orders").replace('{{count}}', String(selectedCount)),
        onClick: () => handleImport(selectedOrders),
        disabled: selectedCount === 0 || isImporting,
        loading: isImporting,
        icon: isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />,
      };
    }
    // Upload step - single footer CTA: Review Orders
    return {
      label: t("bulk.reviewOrders") || "Review Orders",
      onClick: handleReviewOrders,
      disabled: !hasUploadContent,
      icon: <Search className="h-4 w-4" />,
    };
  };

  // Dynamic secondary action based on step
  const getSecondaryAction = () => {
    if (step === "parsing") {
      return {
        label: t("common.cancel") || "Cancel",
        onClick: handleCancelParsing,
      };
    }
    if (step === "review") {
      return {
        label: t("bulk.backToUpload"),
        onClick: handleBackToUpload,
      };
    }
    return {
      label: t("common.cancel"),
      onClick: handleClose,
    };
  };

  return (
    <OverlayPage
      title={t("orders.bulkUpload")}
      breadcrumbs={breadcrumbs}
      isDirty={overlay.isDirty}
      isProcessing={overlay.state === "processing"}
      overlayState={overlay}
      onClose={handleClose}
      primaryAction={getPrimaryAction()}
      secondaryAction={getSecondaryAction()}
    >
      <OverlayContent>
        <div className="space-y-4">
          {(step === "upload" || step === "parsing") && (
            <div className="space-y-4">
              {step === "upload" && (
                <>
                  <SegmentedControl
                    value={uploadMode}
                    onValueChange={handleUploadModeChange}
                    options={[
                      { value: "excel", label: t("bulk.uploadTab") },
                      { value: "paste", label: t("bulk.smartPasteTab") },
                    ]}
                  />

                  {uploadMode === "excel" && (
                    <ExcelUploadTab 
                      ref={excelTabRef}
                      onParsed={handleParsed} 
                      onProgress={setParseProgress}
                      onContentChange={setHasUploadContent}
                    />
                  )}
                  {uploadMode === "paste" && (
                    <SmartPasteTab 
                      ref={smartPasteTabRef}
                      onParsed={handleParsed}
                      onProgress={setParseProgress}
                      onContentChange={setHasUploadContent}
                    />
                  )}
                </>
              )}
              
              {step === "parsing" && (
                <BulkParsingOverlay
                  estimatedCount={estimatedCount}
                  progress={parseProgress}
                  onCancel={handleCancelParsing}
                  canCancel={true}
                />
              )}
            </div>
          )}

          {step === "review" && (
            <OrderReviewTableEnhanced
              orders={parsedOrders}
              summary={summary}
              onImport={handleImport}
              onBack={handleBackToUpload}
              isImporting={isImporting}
              onSelectionChange={handleSelectionChange}
            />
          )}

          {step === "complete" && importResult && (
            <BulkImportSummary result={importResult} />
          )}
        </div>
      </OverlayContent>
    </OverlayPage>
  );
}
